import { Messaging, onRegistered, register } from 'firebase/messaging'
import { firebaseConfig, getFirebaseMessaging, isFirebaseConfigured } from './firebaseApp'

type FidListener = (fid: string) => void

const listeners = new Set<FidListener>()
let handlerInstalado = false

// O SDK aceita um único onRegistered por instância; este repassa o FID para os listeners.
const instalarHandler = (messaging: Messaging) => {
  if (handlerInstalado) return
  handlerInstalado = true
  onRegistered(messaging, (fid) => listeners.forEach((listener) => listener(fid)))
}

export const onPushFidChange = (listener: FidListener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const urlServiceWorker = () => {
  const config = Object.entries(firebaseConfig).filter(
    (param): param is [string, string] => typeof param[1] === 'string'
  )
  return `/firebase-messaging-sw.js?${new URLSearchParams(config)}`
}

// Sem esperar o worker ativar, o subscribe falha no primeiro acesso do navegador.
const aguardarAtivo = (registration: ServiceWorkerRegistration): Promise<void> => {
  const worker = registration.installing || registration.waiting
  if (registration.active || !worker) return Promise.resolve()

  return new Promise((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated' || worker.state === 'redundant') resolve()
    })
  })
}

// undefined no Safari do iPhone em aba comum (push só com o site instalado na Tela de Início).
export const permissaoNotificacao = (): NotificationPermission | undefined =>
  typeof Notification === 'undefined' ? undefined : Notification.permission

// Chamar direto no clique, antes de qualquer await: o Safari recusa pedido fora de gesto do usuário.
export const pedirPermissaoNotificacao = async (): Promise<NotificationPermission | undefined> => {
  if (permissaoNotificacao() === 'default') {
    await Notification.requestPermission()
  }
  return permissaoNotificacao()
}

export const getPushFid = async ({ pedirPermissao = false } = {}): Promise<string | undefined> => {
  if (!isFirebaseConfigured) return undefined

  try {
    const permissao = pedirPermissao ? await pedirPermissaoNotificacao() : permissaoNotificacao()
    if (permissao !== 'granted') return undefined

    const messaging = await getFirebaseMessaging()
    if (!messaging) return undefined

    const registration = await navigator.serviceWorker.register(urlServiceWorker())
    await aguardarAtivo(registration)

    instalarHandler(messaging)

    let fid: string | undefined
    const pararDeOuvir = onPushFidChange((novoFid) => {
      fid = novoFid
    })
    try {
      await register(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      })
    } finally {
      pararDeOuvir()
    }

    return fid
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Não foi possível registrar o navegador para notificações push.', error)
    return undefined
  }
}
